import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Navbar, Footer } from '../components';
import api from '../utils/api';

const TicketDetail = () => {
  const { id } = useParams();
  const { isAuthenticated, user: currentUser } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reply state
  const [replyMessage, setReplyMessage] = useState('');
  const [replyAttachment, setReplyAttachment] = useState(null);
  const [submittingReply, setSubmittingReply] = useState(false);

  // Rating state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(5);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);

  // Close ticket state
  const [closing, setClosing] = useState(false);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    fetchTicket();
  }, [id, isAuthenticated, navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTicket = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/tickets/${id}`);
      setTicket(res.data);
    } catch (err) {
      toast.error('Gagal memuat detail tiket');
      navigate('/support');
    } finally {
      setLoading(false);
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return toast.error("Pesan balasan harus diisi");

    setSubmittingReply(true);
    try {
      const fd = new FormData();
      fd.append('message', replyMessage);
      if (replyAttachment) {
        fd.append('attachment', replyAttachment);
      }

      await api.post(`/api/tickets/${id}/reply`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success("Balasan terkirim");
      setReplyMessage('');
      setReplyAttachment(null);
      fetchTicket(); // Reload ticket to get new reply
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal mengirim balasan");
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!window.confirm("Apakah Anda yakin ingin menutup tiket ini? Tiket yang sudah ditutup tidak dapat dibalas kembali.")) return;

    setClosing(true);
    try {
      await api.post(`/api/tickets/${id}/close`);
      toast.success("Tiket berhasil ditutup");
      fetchTicket();
      setShowRatingModal(true); // Offer to rate right after closing
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal menutup tiket");
    } finally {
      setClosing(false);
    }
  };

  const handleRateSubmit = async (e) => {
    e.preventDefault();
    setSubmittingRating(true);
    try {
      await api.post(`/api/tickets/${id}/rate`, {
        rating,
        rating_comment: ratingComment
      });
      toast.success("Terima kasih atas penilaian Anda!");
      setShowRatingModal(false);
      fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal mengirim penilaian");
    } finally {
      setSubmittingRating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('id-ID', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const statusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-amber-500/15 text-amber-500 ring-amber-500/30';
      case 'answered': return 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30';
      case 'closed': return 'bg-zinc-500/15 text-zinc-400 ring-zinc-500/30';
      default: return 'bg-zinc-500/15 text-zinc-400 ring-zinc-500/30';
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-zinc-950 text-white pb-20 pt-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-140px)]">
          
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shrink-0">
            <div>
              <Link to="/support" className="inline-flex items-center text-sm font-semibold text-zinc-400 hover:text-white mb-3 transition">
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Kembali ke Daftar Tiket
              </Link>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-extrabold tracking-tight">
                  {loading ? 'Memuat tiket...' : ticket?.subject}
                </h1>
                {ticket && (
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${statusColor(ticket.status)}`}>
                    {ticket.status.toUpperCase()}
                  </span>
                )}
              </div>
              {ticket && (
                <p className="mt-1 text-sm text-zinc-400">
                  Tiket #{ticket.id.toString().padStart(5, '0')} • Dibuat pada {formatDate(ticket.created_at)}
                </p>
              )}
            </div>
            
            {ticket && ticket.status !== 'closed' && (
              <button
                onClick={handleCloseTicket}
                disabled={closing}
                className="inline-flex items-center justify-center rounded-xl bg-rose-500/10 px-4 py-2 text-sm font-bold text-rose-500 ring-1 ring-rose-500/20 hover:bg-rose-500 hover:text-white transition disabled:opacity-50 shrink-0"
              >
                {closing ? 'Menutup...' : 'Selesaikan & Tutup Tiket'}
              </button>
            )}
            {ticket && ticket.status === 'closed' && ticket.rating === null && (
               <button
                 onClick={() => setShowRatingModal(true)}
                 className="inline-flex items-center justify-center rounded-xl bg-emerald-500/10 px-4 py-2 text-sm font-bold text-emerald-500 ring-1 ring-emerald-500/20 hover:bg-emerald-500 hover:text-white transition shrink-0"
               >
                 Beri Penilaian (CSAT)
               </button>
            )}
          </div>

          <div className="flex-1 rounded-3xl border border-white/10 bg-zinc-900/50 flex flex-col overflow-hidden">
            {loading ? (
              <div className="flex-1 flex justify-center items-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-800 border-t-emerald-500"></div>
              </div>
            ) : (
              <>
                {/* Chat History */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
                  {ticket?.replies?.map((reply, index) => {
                    const isStaff = reply.user?.role === 'super_admin' || reply.user?.role === 'admin';
                    const isMe = reply.user_id === currentUser?.id;
                    const btoa = (str) => Buffer.from(str).toString('base64');
                    const imgUrl = reply.attachment_path ? `${process.env.REACT_APP_BACKEND_URL || 'https://apistore.iqbaldeveloper.my.id'}/storage/${reply.attachment_path}` : null;
                    
                    return (
                      <div key={reply.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                        <div className={`flex items-center gap-2 text-xs text-zinc-400 mb-1 px-1 ${isMe && 'flex-row-reverse'}`}>
                           <span className="font-bold text-zinc-300">
                             {isMe ? 'Anda' : (isStaff ? `${reply.user?.name} (Staff)` : reply.user?.name)}
                           </span>
                           <span>•</span>
                           <span>{formatDate(reply.created_at)}</span>
                        </div>
                        <div 
                          className={`max-w-[85%] sm:max-w-[75%] rounded-2xl p-4 ${
                            isMe 
                              ? 'bg-emerald-600 text-white rounded-tr-sm' 
                              : isStaff
                                ? 'bg-zinc-800 text-white ring-1 ring-white/10 rounded-tl-sm'
                                : 'bg-zinc-800 text-white rounded-tl-sm'
                          }`}
                        >
                          <p className="whitespace-pre-wrap text-sm leading-relaxed">{reply.message}</p>
                          
                          {imgUrl && (
                            <div className="mt-3">
                              <a href={imgUrl} target="_blank" rel="noopener noreferrer" className="block max-w-xs rounded-xl overflow-hidden border border-white/20">
                                {/* Simple check for PDF vs Image */}
                                {imgUrl.toLowerCase().endsWith('.pdf') ? (
                                  <div className="bg-white/10 p-4 flex items-center justify-center gap-2">
                                     <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M7 18h10v-2H7v2zM17 14H7v-2h10v2zM8 3h4.414c.53 0 1.039.21 1.414.586l4.586 4.586c.375.375.586.884.586 1.414V21c0 1.1-.9 2-2 2H8c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2zM8 21h8V9h-5V5H8v16z"/></svg>
                                     <span className="text-sm font-semibold truncate">Tampilkan Dokumen</span>
                                  </div>
                                ) : (
                                  <img src={imgUrl} alt="Attachment" className="w-full h-auto" />
                                )}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                  {ticket?.status === 'closed' && (
                    <div className="flex justify-center mt-6">
                      <div className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-xs font-semibold text-zinc-400 inline-flex items-center gap-2">
                        <svg className="w-4 h-4 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Tiket ini telah ditutup
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Form */}
                {ticket?.status !== 'closed' && (
                  <div className="border-t border-white/10 p-4 sm:p-6 bg-zinc-900 border-b-0 rounded-b-3xl">
                    <form onSubmit={handleReplySubmit} className="flex flex-col sm:flex-row items-end gap-3">
                      <div className="flex-1 w-full bg-white/5 border border-white/10 rounded-2xl p-2 relative focus-within:ring-1 focus-within:ring-emerald-500/50 transition">
                        <textarea
                          rows="2"
                          placeholder="Ketik pesan balasan Anda..."
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          className="w-full bg-transparent p-2 text-sm text-white placeholder-zinc-500 outline-none resize-none"
                        />
                        <div className="flex justify-between items-center px-2 pb-1">
                          <input
                            type="file"
                            id="attachment-reply"
                            className="hidden"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => setReplyAttachment(e.target.files[0])}
                          />
                          <label htmlFor="attachment-reply" className={`cursor-pointer inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-lg transition ${replyAttachment ? 'bg-emerald-500/10 text-emerald-400' : 'text-zinc-400 hover:bg-white/5 hover:text-white'}`}>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            {replyAttachment ? replyAttachment.name : 'Lampirkan file'}
                          </label>
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={submittingReply || !replyMessage.trim()}
                        className="w-full sm:w-auto self-start sm:self-end h-[100px] sm:h-[48px] rounded-2xl bg-emerald-500 px-6 font-bold text-white shadow-sm hover:bg-emerald-400 transition disabled:opacity-50 shrink-0 flex items-center justify-center gap-2"
                      >
                        {submittingReply ? 'Mengirim...' : (
                          <>
                            Kirim
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                            </svg>
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* CSAT Rating Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-[2rem] bg-zinc-900 border border-zinc-800 p-8 text-center">
            <h2 className="text-2xl font-extrabold text-white mb-2">Bantu Kami Lebih Baik!</h2>
            <p className="text-zinc-400 text-sm mb-6">Seberapa puas Anda dengan pelayanan kami pada tiket ini?</p>
            
            <form onSubmit={handleRateSubmit}>
              <div className="flex justify-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="focus:outline-none transition-transform hover:scale-110 active:scale-95"
                  >
                    <svg className={`w-10 h-10 ${star <= rating ? 'text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]' : 'text-zinc-700'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.173c.969 0 1.371 1.24.588 1.81l-3.376 2.454a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.538 1.118l-3.376-2.454a1 1 0 00-1.176 0l-3.376 2.454c-.783.57-1.838-.196-1.538-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.173a1 1 0 00.95-.69l1.286-3.967z" />
                    </svg>
                  </button>
                ))}
              </div>

              <textarea
                rows="3"
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder="Ada saran atau masukan tambahan? (Opsional)"
                className="w-full rounded-2xl bg-white/5 p-4 text-sm text-white ring-1 ring-white/10 focus:ring-amber-500 outline-none resize-none mb-6"
              />

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowRatingModal(false)}
                  className="flex-1 rounded-xl bg-white/5 py-3 text-sm font-bold text-zinc-300 hover:bg-white/10 transition"
                >
                  Lewati
                </button>
                <button
                  type="submit"
                  disabled={submittingRating}
                  className="flex-1 rounded-xl bg-amber-500 py-3 text-sm font-bold text-amber-950 shadow-[0_0_15px_rgba(251,191,36,0.3)] hover:bg-amber-400 transition disabled:opacity-50"
                >
                  {submittingRating ? 'Mengirim...' : 'Kirim Penilaian'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </>
  );
};

export default TicketDetail;
